import React from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import {
  Stethoscope, FlaskConical, Leaf, ArrowUpRight, Download, CheckCircle2,
  Sparkles, ShieldCheck,
} from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { useFetch } from "@/lib/useData";
import { ASSET } from "@/lib/api";
import RichHtml from "@/components/RichHtml";

const ICONS = { stethoscope: Stethoscope, "flask-conical": FlaskConical, leaf: Leaf };
const ACCENTS = {
  navy: { hero: "from-navy via-navy-dark to-navy", bar: "bg-medical-blue", chip: "bg-medical-blue/10 text-medical-blue", text: "text-medical-blue", icon: Stethoscope },
  teal: { hero: "from-navy via-navy-dark to-teal-600", bar: "bg-teal", chip: "bg-teal/10 text-teal", text: "text-teal", icon: FlaskConical },
  wellness: { hero: "from-navy via-emerald-900 to-emerald-700", bar: "bg-wellness-green", chip: "bg-wellness-green/15 text-wellness-green", text: "text-wellness-green", icon: Leaf },
};

export default function Division() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const { data } = useFetch(`/divisions/${slug}`, [slug]);
  if (!data) return <div className="min-h-screen pt-32" />;

  const accentKey = data.accent_color === "wellness" || data.secondary_accent === "wellness-green"
    ? "wellness"
    : data.accent_color === "teal"
      ? "teal"
      : "navy";
  const A = ACCENTS[accentKey];
  const Icon = ICONS[data.icon] || A.icon;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    name: data.name,
    description: data.overview,
    url: `https://infinitymg.so${pathname}`,
    parentOrganization: { "@type": "MedicalOrganization", name: "Infinity Medical Group" },
    medicalSpecialty: (data.services || []).map((s) => s.title),
  };

  return (
    <>
      <SEO
        title={`${data.name} | Infinity Medical Group`}
        description={data.summary}
        path={pathname}
        jsonLd={jsonLd}
      />
      <Breadcrumbs overrideLast={data.name} />

      {/* HERO */}
      <section data-testid={`division-hero-${slug}`} className={`relative pt-6 pb-24 lg:pb-28 bg-gradient-to-br ${A.hero} text-white overflow-hidden`}>
        <div className="img-grain absolute inset-0" />
        {data.image && (
          <img
            src={ASSET(data.image)}
            alt={data.name}
            className="absolute inset-0 h-full w-full object-cover opacity-15"
          />
        )}
        <div className="container relative">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] font-semibold text-teal-light">
            <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10`}>
              <Icon className="h-4 w-4" />
            </span>
            <span>{data.tagline}</span>
          </div>
          <h1 className="mt-7 font-display font-bold text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
            {data.name}
          </h1>
          {data.overview && (
            <div className="mt-7 max-w-3xl text-white/90 [&_*]:text-white/90 [&_strong]:text-white [&_a]:text-teal-light [&_a:hover]:text-white">
              <RichHtml html={data.overview} testId="division-overview-hero" />
            </div>
          )}
          <div className={`mt-8 h-1 w-16 ${A.bar} rounded-full`} />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/contact?division=${slug}`}
              data-testid="division-cta-consult"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal text-white text-sm font-semibold hover:bg-teal-light hover:text-navy transition-colors">
              Request a consultation <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="bg-white py-20 lg:py-24">
        <div className="container grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <span className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold ${accentKey === "teal" ? "text-teal" : "text-medical-blue"}`}>
              <span className={`h-px w-6 ${A.bar}`} /> Division overview
            </span>
            <h2 className="mt-5 font-display font-semibold text-3xl md:text-4xl text-navy-800 tracking-tight">
              Comprehensive, patient-centered care.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <p className="text-lg leading-relaxed text-navy-700/80">{data.extended_body || data.body}</p>
          </Reveal>
        </div>
      </section>

      {/* CAPABILITIES */}
      {data.capabilities?.length > 0 && (
        <section className="bg-soft-gray py-20 lg:py-24" data-testid="division-capabilities">
          <div className="container">
            <h2 className="font-display font-semibold text-3xl md:text-4xl text-navy-800 tracking-tight max-w-2xl">
              Key capabilities
            </h2>
            <Stagger className="mt-12 grid gap-4 md:grid-cols-2">
              {data.capabilities.map((c, i) => (
                <Item key={c}>
                  <div className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-navy-50">
                    <CheckCircle2 className={`h-5 w-5 shrink-0 mt-0.5 ${accentKey === "teal" ? "text-teal" : "text-medical-blue"}`} />
                    <p className="text-base text-navy-800">{c}</p>
                  </div>
                </Item>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {/* SERVICES */}
      {data.services?.length > 0 && (
        <section className="bg-white py-20 lg:py-24" data-testid="division-services">
          <div className="container">
            <h2 className="font-display font-semibold text-3xl md:text-4xl text-navy-800 tracking-tight max-w-2xl">
              Services offered
            </h2>
            <Stagger className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {data.services.map((s) => (
                <Item key={s.slug}>
                  <Link to={`/services/${s.slug}`}
                    data-testid={`division-service-${s.slug}`}
                    className="block h-full p-7 rounded-2xl bg-soft-gray border border-navy-50 hover:bg-white hover:shadow-card transition-all">
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${A.chip}`}>
                      <Stethoscope className="h-4 w-4" />
                    </span>
                    <h3 className="mt-5 font-display font-semibold text-lg text-navy-800">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-navy-700/75">{s.summary}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-teal">
                      Learn more <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </Link>
                </Item>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {/* GALLERY */}
      {data.gallery?.length > 0 && (
        <section className="bg-soft-gray py-20 lg:py-24" data-testid="division-gallery">
          <div className="container">
            <h2 className="font-display font-semibold text-3xl md:text-4xl text-navy-800 tracking-tight max-w-2xl">
              From our practice
            </h2>
            <Stagger className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {data.gallery.filter(Boolean).map((url, i) => {
                const alt = data.gallery_alts?.[i] || `${data.name} — photograph ${i + 1}`;
                return (
                  <Item key={url + i}>
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-navy-50">
                      <img
                        src={ASSET(url)}
                        alt={alt}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => { e.currentTarget.parentElement.parentElement.style.display = "none"; }}
                        className="h-full w-full object-cover hover:scale-[1.04] transition-transform duration-700"
                      />
                    </div>
                  </Item>
                );
              })}
            </Stagger>
            {data.gallery_credit && (
              <p className="mt-6 text-[11px] uppercase tracking-[0.18em] text-navy-400">{data.gallery_credit}</p>
            )}
          </div>
        </section>
      )}

      {/* WHY THIS DIVISION */}
      {data.why_points?.length > 0 && (
        <section className="bg-navy text-white py-20 lg:py-24 relative overflow-hidden">
          <div className="img-grain absolute inset-0" />
          <div className="container relative">
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">
              Why choose this division
            </h2>
            <Stagger className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {data.why_points.map((w) => (
                <Item key={w}>
                  <div className="p-6 rounded-2xl bg-white/[0.04] border border-white/10 h-full">
                    <Sparkles className="h-5 w-5 text-teal-light" />
                    <p className="mt-4 text-base text-white/90 leading-relaxed">{w}</p>
                  </div>
                </Item>
              ))}
            </Stagger>
          </div>
        </section>
      )}
    </>
  );
}
